import { useEffect } from 'react';

import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { useForm } from '@/components/react-hook-form';
import {
  MemoDetails_MemoFragment,
  MemoDetailsDocument,
} from '@/modules/memos/graphql/MemoDetails.generated';
import { useUpdateMemoMutation } from '@/modules/memos/graphql/UpdateMemo.generated';

import { MemoDetailsFormShape } from '../MemoDetails.types';

interface UseMemoNameFormParams {
  memo: MemoDetails_MemoFragment | null;
  canEditHouseholdData: boolean;
}

export function useMemoNameForm({
  memo,
  canEditHouseholdData,
}: UseMemoNameFormParams) {
  const { showFeedback } = useFeedback();
  const formMethods = useForm<MemoDetailsFormShape>({
    defaultValues: {
      memoName: '',
    },
  });

  const {
    getValues,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = formMethods;

  const [updateMemo, { loading: savingMemoName }] = useUpdateMemoMutation({
    refetchQueries: [MemoDetailsDocument],
    awaitRefetchQueries: true,
    onCompleted: () => {
      showFeedback(FeedbackMessages.formSaveSuccess, {
        variant: 'success',
      });
    },
    onError: () => {
      showFeedback(FeedbackMessages.formSaveError);
    },
  });

  useEffect(() => {
    if (!memo) {
      return;
    }

    if (isDirty) {
      return;
    }

    const currentMemoName = getValues('memoName');
    if (currentMemoName === memo.name) {
      return;
    }

    reset({
      memoName: memo.name,
    });
  }, [getValues, isDirty, memo, reset]);

  const handleValidSubmit = async (form: MemoDetailsFormShape) => {
    if (!canEditHouseholdData || !memo) {
      return;
    }

    const nextMemoName = form.memoName.trim();

    if (!nextMemoName) {
      return;
    }

    if (nextMemoName === memo.name) {
      reset({
        memoName: memo.name,
      });
      return;
    }

    const { data } = await updateMemo({
      variables: {
        input: {
          id: memo.id,
          update: {
            name: nextMemoName,
          },
        },
      },
    });

    if (!data?.updateMemo.id) {
      return;
    }

    reset({
      memoName: nextMemoName,
    });
  };

  const onSubmit = handleSubmit(handleValidSubmit);

  return {
    formMethods,
    onSubmit,
    isDirty,
    savingMemoName,
  };
}
